import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import { CATEGORY_FILL } from '../../components/agent/scheduleDay'
import { formatScheduleLabel, useTimeFormat } from '../../lib/timeFormat'
import type { WeekdayDefault } from './scheduleDefaults'
import { supabase } from '../../lib/supabase'
import { getMyScheduleDefault, type EmployeeScheduleDefault } from '../supervisor/scheduleDefaultStore'
import { useProfileIdentity } from '../../lib/useProfileIdentity'

function ArrowLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  )
}

// No date or day-number here, unlike DayCard: a default repeats every week
// rather than landing on a specific calendar day, so there is nothing to
// square off like a day-of-month.
function WeekdayRow({ day, time }: { day: WeekdayDefault; time: string }) {
  const { format } = useTimeFormat()
  return (
    <div className={`flex items-center justify-between gap-3 rounded-surface border border-line p-3 ${CATEGORY_FILL[day.category]}`}>
      <p className="text-small font-bold">{day.weekday}</p>
      {day.category === 'off' ? (
        <p className="text-small text-ink-muted">Regular day off</p>
      ) : (
        <div className="text-right">
          <p className="text-small font-bold [font-variant-numeric:tabular-nums]">
            {formatScheduleLabel(time, format)}
          </p>
          {day.assignment && <p className="mt-0.5 text-caption text-ink-muted">{day.assignment}</p>}
        </div>
      )}
    </div>
  )
}

export default function AgentScheduleTemplatePage() {
  const { format } = useTimeFormat()
  const identity = useProfileIdentity()
  // The agent's own week, read from the tables a supervisor writes it to:
  // null until it loads, and again for an account with no employee record.
  const [scheduleDefault, setScheduleDefault] = useState<EmployeeScheduleDefault | null>(null)

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const id = session?.user.id
      if (!id) return
      const loaded = await getMyScheduleDefault(id)
      if (!cancelled) setScheduleDefault(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const shift = scheduleDefault?.shift ?? null
  return (
    <div className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0">
      <a
        href="#schedule-template-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="schedule-template-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-4 p-4 lg:gap-6 lg:p-8">
          <Link
            to="/agent"
            className="flex min-h-11 items-center gap-1.5 self-start rounded-control text-small font-bold text-ink-muted"
          >
            <ArrowLeftIcon />
            Dashboard
          </Link>

          <div>
            <h1 className="text-h3 font-extrabold lg:text-h2">Your Normal Schedule</h1>
            <p className="mt-0.5 text-small text-ink-muted">
              What a new pay period is built from when there is nothing to copy.
            </p>
          </div>

          <div className="flex items-center gap-3 rounded-surface border border-line bg-surface-2 p-3.5">
            <span className="flex size-9 flex-none items-center justify-center rounded-full bg-canvas text-ink-muted">
              <ClockIcon />
            </span>
            <div>
              <p className="text-small font-bold">
                {shift ? `${shift.name} · ${formatScheduleLabel(shift.time, format)}` : 'No shift template'}
              </p>
              {shift && <p className="mt-0.5 text-caption text-ink-muted">Applies {shift.appliesTo}</p>}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {(scheduleDefault?.weekly ?? []).map((day) => (
              <WeekdayRow key={day.weekday} day={day} time={shift?.time ?? ''} />
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
